"use client";

import { useCallback } from "react";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import type { ApiPiece } from "@/hooks/use-projects";
import { KanbanCard } from "./kanban-card";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface KanbanColumn {
  id: string;
  color: string;
}

const COLUMNS: KanbanColumn[] = [
  { id: "DRAFT", color: "bg-zinc-500" },
  { id: "PENDING_REVIEW", color: "bg-amber-500" },
  { id: "APPROVED", color: "bg-green-500" },
  { id: "REJECTED", color: "bg-red-500" },
];

interface KanbanBoardProps {
  pieces: ApiPiece[];
  onPieceClick: (piece: ApiPiece) => void;
  onSubmit: (pieceId: string) => void;
  onApprove: (pieceId: string) => void;
  onReject: (pieceId: string) => void;
  canApprove: boolean;
}

export function KanbanBoard({ pieces, onPieceClick, onSubmit, onApprove, onReject, canApprove }: KanbanBoardProps) {
  const { t } = useTranslation("pieces");
  const grouped = COLUMNS.reduce(
    (acc, col) => {
      acc[col.id] = pieces.filter((p) => p.status === col.id);
      return acc;
    },
    {} as Record<string, ApiPiece[]>,
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const sourceCol = result.source.droppableId;
      const destCol = result.destination.droppableId;
      const pieceId = result.draggableId;

      if (sourceCol === destCol) return;

      if (sourceCol === "DRAFT" && destCol === "PENDING_REVIEW") {
        onSubmit(pieceId);
      } else if (sourceCol === "PENDING_REVIEW" && destCol === "APPROVED" && canApprove) {
        onApprove(pieceId);
      } else if (sourceCol === "PENDING_REVIEW" && destCol === "REJECTED" && canApprove) {
        onReject(pieceId);
      }
      // Invalid transitions: piece snaps back (no action)
    },
    [onSubmit, onApprove, onReject, canApprove],
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <div key={col.id} className="flex flex-col rounded-lg bg-muted/50 p-3">
            <div className="mb-3 flex items-center gap-2">
              <div className={cn("h-2 w-2 rounded-full", col.color)} />
              <span className="text-sm font-medium">{t(`pieces:kanban.columns.${col.id}`)}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {grouped[col.id]?.length ?? 0}
              </span>
            </div>

            <Droppable droppableId={col.id}>
              {(provided, snapshot) => (
                <ScrollArea className="flex-1">
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "min-h-[100px] space-y-2 rounded-md p-1 transition-colors",
                      snapshot.isDraggingOver && "bg-accent/30",
                    )}
                  >
                    {(grouped[col.id] ?? []).map((piece, index) => (
                      <Draggable key={piece.id} draggableId={piece.id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <KanbanCard
                              piece={piece}
                              onClick={() => onPieceClick(piece)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </ScrollArea>
              )}
            </Droppable>
          </div>
        ))}
      </div>
    </DragDropContext>
  );
}
